// Building blocks for the Sales Performance module — one consistent, premium
// look: soft cards, clear hierarchy, green (achieved) / amber (near) / red
// (below) / blue (company figures) and nothing louder.
import type { ReactNode } from 'react';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';
import { X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export const C = { blue: '#2563eb', blueSoft: '#dbeafe', green: '#059669', greenSoft: '#d1fae5', amber: '#d97706', amberSoft: '#fef3c7', red: '#e11d48', redSoft: '#ffe4e6', slate: '#64748b', violet: '#7c3aed' };

export function toneFor(pct: number | null | undefined) {
  if (pct === null || pct === undefined) return { text: 'text-slate-400', bar: '#cbd5e1', soft: 'bg-slate-100 text-slate-500' };
  if (pct >= 100) return { text: 'text-emerald-600', bar: C.green, soft: 'bg-emerald-50 text-emerald-700' };
  if (pct >= 80) return { text: 'text-amber-600', bar: C.amber, soft: 'bg-amber-50 text-amber-700' };
  return { text: 'text-rose-600', bar: C.red, soft: 'bg-rose-50 text-rose-700' };
}

export function Panel({ title, subtitle, icon: Icon, right, children, className = '', pad = true }: {
  title?: string; subtitle?: string; icon?: LucideIcon; right?: ReactNode; children: ReactNode; className?: string; pad?: boolean;
}) {
  return (
    <section className={`rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)] ${className}`}>
      {(title || right) && (
        <header className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div className="flex items-start gap-3">
            {Icon && <span className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600"><Icon className="h-4 w-4" /></span>}
            <div>
              {title && <h3 className="text-[15px] font-semibold text-slate-900">{title}</h3>}
              {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
            </div>
          </div>
          {right}
        </header>
      )}
      <div className={pad ? 'p-5' : ''}>{children}</div>
    </section>
  );
}

/** A KPI tile: label, big value, a small trend line and the change vs the previous period. */
export function Kpi({ label, value, sub, icon: Icon, accent = C.blue, spark, delta, progress }: {
  label: string; value: ReactNode; sub?: ReactNode; icon?: LucideIcon; accent?: string;
  spark?: number[]; delta?: number | null; progress?: number | null;
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-start justify-between">
        <p className="text-data-xs font-semibold uppercase tracking-[0.08em] text-slate-500">{label}</p>
        {Icon && <span className="flex h-8 w-8 items-center justify-center rounded-xl" style={{ background: `${accent}14`, color: accent }}><Icon className="h-4 w-4" /></span>}
      </div>
      <p className="mt-1.5 text-[26px] font-bold leading-tight tracking-tight text-slate-900 tabular-nums">{value}</p>
      <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
        {delta !== undefined && delta !== null && (
          <span className={`rounded-md px-1.5 py-0.5 font-semibold ${delta >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
            {delta >= 0 ? '▲' : '▼'} {Math.abs(delta)}%
          </span>
        )}
        {sub && <span className="truncate">{sub}</span>}
      </div>
      {progress !== undefined && progress !== null && (
        <div className="mt-3 h-1.5 rounded-full bg-slate-100"><div className="h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, Math.max(0, progress))}%`, background: toneFor(progress).bar }} /></div>
      )}
      {spark && spark.length > 1 && progress === undefined && (
        <div className="-mx-1 mt-2 h-9">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={spark.map((v, i) => ({ i, v }))} margin={{ top: 2, bottom: 0, left: 0, right: 0 }}>
              <defs><linearGradient id={`sp-${label.replace(/\W/g, '')}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={accent} stopOpacity={0.28} /><stop offset="100%" stopColor={accent} stopOpacity={0} /></linearGradient></defs>
              <Area type="monotone" dataKey="v" stroke={accent} strokeWidth={1.75} fill={`url(#sp-${label.replace(/\W/g, '')})`} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export function Initials({ name, size = 32, tone = C.blue }: { name: string; size?: number; tone?: string }) {
  const init = name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';
  return <span className="inline-flex shrink-0 items-center justify-center rounded-full font-semibold" style={{ width: size, height: size, fontSize: size * 0.38, background: `${tone}18`, color: tone }}>{init}</span>;
}

export function Bar({ pct }: { pct: number | null | undefined }) {
  const t = toneFor(pct);
  return <div className="h-1.5 w-24 rounded-full bg-slate-100"><div className="h-1.5 rounded-full" style={{ width: `${Math.min(100, Math.max(0, pct ?? 0))}%`, background: t.bar }} /></div>;
}

export function Chip({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-data-xs font-semibold ${className}`}>{children}</span>;
}

export function Dialog({ open, title, subtitle, onClose, children, footer, width = 'max-w-lg' }: {
  open: boolean; title: string; subtitle?: string; onClose: () => void; children: ReactNode; footer?: ReactNode; width?: string;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-[2px]" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`w-full ${width} overflow-hidden rounded-2xl bg-white shadow-2xl`}>
        <div className="flex items-start justify-between border-b border-slate-100 px-5 py-4">
          <div><h3 className="text-base font-semibold text-slate-900">{title}</h3>{subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}</div>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50/60 px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      {children}
      {hint && <span className="text-data-xs text-slate-400">{hint}</span>}
    </label>
  );
}

export const inputCls = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/15 disabled:bg-slate-50 disabled:text-slate-500';

export function SelectPill({ label, value, onChange, options, allLabel }: {
  label: string; value: string; onChange: (v: string) => void; options: Array<{ value: string; label: string }>; allLabel?: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-data-xs font-semibold uppercase tracking-[0.08em] text-slate-400">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="min-w-[8.5rem] rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[13px] font-medium text-slate-800 outline-none focus:border-blue-500">
        {allLabel !== undefined && <option value="">{allLabel}</option>}
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

export function Empty({ title, text, action }: { title: string; text?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-12 text-center">
      <p className="text-sm font-semibold text-slate-800">{title}</p>
      {text && <p className="max-w-md text-xs text-slate-500">{text}</p>}
      {action}
    </div>
  );
}
